import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, MessageSquare, Trophy, Award, Home as HomeIcon } from 'lucide-react';

const HomePage = () => {
  const location = useLocation();
  
  const navItems = [
    { path: '/', label: 'Início', icon: HomeIcon },
    { path: '/programacao', label: 'Programação', icon: Calendar },
    { path: '/comunicados', label: 'Comunicados', icon: MessageSquare },
    { path: '/ranking', label: 'Ranking', icon: Award },
    { path: '/quiz', label: 'Quiz', icon: Trophy },
  ];

  const isActive = (path) => location.pathname === path;

  const features = [
    {
      icon: Calendar,
      title: 'Programação Completa',
      description: 'Confira todas as atividades, palestras, mesas redondas e oficinas do evento.',
      link: '/programacao',
      color: 'from-amazonia-green-500 to-amazonia-green-600'
    },
    {
      icon: MessageSquare,
      title: 'Comunicados',
      description: 'Fique por dentro de todos os avisos e atualizações importantes.',
      link: '/comunicados',
      color: 'from-golden-500 to-golden-600'
    },
    {
      icon: Award,
      title: 'Ranking da Caneta Dourada',
      description: 'Veja os melhores desempenhos no quiz e compare sua pontuação.',
      link: '/ranking',
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: Trophy,
      title: 'Quiz - Caneta Dourada',
      description: 'Participe da jornada gamificada e teste seus conhecimentos sobre o ICSA.',
      link: '/quiz',
      color: 'from-golden-600 to-golden-700'
    }
  ];

  const stats = [
    { number: '8ª', label: 'Edição do Evento' },
    { number: '2', label: 'Dias de Programação' },
    { number: '10', label: 'Atividades Programadas' },
    { number: '300+', label: 'Participantes Esperados' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-amazonia-green-600 via-amazonia-green-700 to-amazonia-dark-700 text-white py-12 md:py-20">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="mb-6 md:mb-8">
                <div className="w-28 h-28 md:w-28 md:h-28 mx-auto bg-white rounded-full flex items-center justify-center mb-4 md:mb-6 shadow-xl">
                  <img 
                    src="/logo.png" 
                    alt="Logo ICSA" 
                    className="w-24 h-24 md:w-24 md:h-24 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />

                </div>
              </div>
              
              <h1 className="text-2xl md:text-4xl lg:text-6xl font-bold mb-4 md:mb-6">
                8ª Semana Acadêmica
                <span className="block text-golden-400">ICSA - Unifesspa</span>
              </h1>
              
              <p className="text-base md:text-xl lg:text-2xl mb-6 md:mb-8 max-w-4xl mx-auto leading-relaxed px-4">
                <span className="font-semibold text-golden-300">Universidade na Amazônia:</span>
                <br className="hidden sm:block" />
                <span className="sm:inline"> </span>Inovação, Sustentabilidade e Saberes na Rota da COP 30
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/programacao"
                  className="bg-golden-500 hover:bg-golden-600 text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center space-x-2"
                >
                  <Calendar className="w-5 h-5" />
                  <span>Ver Programação</span>
                </Link>
                <Link
                  to="/quiz"
                  className="bg-white text-amazonia-green-700 hover:bg-gray-100 font-bold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center space-x-2"
                >
                  <Trophy className="w-5 h-5" />
                  <span>Jogar Quiz</span>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-bold text-amazonia-green-600 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-br from-amazonia-green-50 to-earth-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-amazonia-dark-800 mb-4">
              Explore o Evento
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Descubra todas as funcionalidades do sistema e aproveite ao máximo a Semana Acadêmica
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2 }}
                  whileHover={{ y: -5 }}
                  className="group"
                >
                  <Link to={feature.link} className="block">
                    <div className="bg-white rounded-xl shadow-lg p-6 h-full transition-all duration-300 group-hover:shadow-2xl border-2 border-transparent group-hover:border-amazonia-green-200">
                      <div className={`w-14 h-14 bg-gradient-to-r ${feature.color} rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                        <IconComponent className="w-7 h-7 text-white" />
                      </div>
                    
                    <h3 className="text-lg font-bold text-amazonia-dark-800 mb-3 group-hover:text-amazonia-green-700 transition-colors">
                      {feature.title}
                    </h3>
                    
                    <p className="text-gray-600 leading-relaxed text-sm">
                      {feature.description}
                    </p>
                    
                    <div className="mt-6 flex items-center text-amazonia-green-600 font-medium group-hover:text-amazonia-green-700">
                      <span>Acessar</span>
                      <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </Link>
              </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
